import * as React from 'react';
import { Input } from 'vet-bones/design-system/components';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { getProjectTitleTestId } from 'src/constants/testIds';
import { projectSelector } from 'src/selectors/project';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectNavbar/projectNavbar.module.scss';

export const ProjectNavbarTitle: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const handleRename = React.useCallback(
    async (name: string) => {
      await dispatch(async (wrappedDispatch) => {
        wrappedDispatch(
          projectsActionCreators.clearedProjectVoiceboxEvaluation({
            id: projectId,
          })
        );
      });
      // must wait for previous action middleware to finish completely
      // before renaming project to avoid cloud sync issues
      dispatch(projectsActionCreators.renamedProject({ id: projectId, name }));
    },
    [dispatch, projectId]
  );

  const value = project?.name || '';
  const [localValue, setLocalValue] = React.useState<string>(value);
  const didValueChange = useDidValueChange(value);
  React.useEffect(() => {
    if (didValueChange) {
      setLocalValue(value);
    }
  }, [didValueChange, value]);

  const onCancel = React.useCallback(() => {
    setLocalValue(value);
  }, [value]);

  const onChange = React.useCallback(
    (newValue: string) => {
      setLocalValue(newValue);
    },
    [setLocalValue]
  );

  const onConfirm = React.useCallback(
    (newValue: string) => {
      const newLabel = newValue.trim();
      if (newLabel) {
        handleRename(newLabel);
      } else {
        // Revert to the prior value if invalid or only empty string / whitespace is left
        setLocalValue(value);
      }
    },
    [handleRename, value]
  );

  const wasEscKeyBlur = React.useRef(false);

  return (
    <div className={styles.projectNavbarTitle}>
      <div>
        <Input
          className={styles.projectNavbarTitleInput}
          clearable={false}
          id="project-navbar-title"
          inputSize={1}
          label=""
          onBlur={() => {
            if (wasEscKeyBlur.current) {
              wasEscKeyBlur.current = false;
              return;
            }

            onConfirm(localValue);
          }}
          onChange={(evt) => onChange(evt.target.value)}
          onClick={(evt) => {
            evt.currentTarget.select();
          }}
          onKeyDown={(evt) => {
            if (evt.key === 'Enter') {
              evt.currentTarget.blur();
            } else if (evt.key === 'Escape') {
              onCancel();
              wasEscKeyBlur.current = true;
              evt.currentTarget.blur();
            }
          }}
          size="md"
          testId={getProjectTitleTestId(project?.id || '')}
          value={localValue}
        />
        <span className={styles.projectNavbarTitleHidden}>{localValue}</span>
      </div>
    </div>
  );
};
